import { SKIP_CHECK_IDENTIFICATION_NUMBERS } from '@insurance/app-constants'
import { DEFAULT_DATE_FORMAT } from '@shared/utils'
import { FormItemProps } from 'antd'
import { RuleObject } from 'antd/es/form'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

type ReturnValidations = Record<
  string,
  | Required<FormItemProps>['rules'][number]
  | ((...args: never[]) => Required<FormItemProps>['rules'][number])
>

export const useValidationsField = () => {
  const { t } = useTranslation()

  return {
    fullName: {
      transform: (value) => value?.trim(),
      pattern: /^[A-Za-z\-'.\s]{2,50}$/,
      message: t('invalid_name_format')
    },
    customPersonalCode: {
      transform: (value) => value?.trim(),
      pattern: /^\d{12,15}$/,
      message: t('invalid_medical_personal_code_format')
    },
    minLength: {
      transform: (value) => value?.trim(),
      pattern: /^.{2,}$/,
      message: t('invalid_minimum_length')
    },
    passportSeriesNumber: {
      transform: (value) => value?.trim(),
      pattern: /^.{7,10}$/,
      message: t('invalid_passport_length')
    },
    required: {
      transform: (value) => (typeof value === 'string' ? value?.trim() : value),
      required: true,
      message: t('this_field_is_required')
    },
    companyName: {
      transform: (value) => value?.trim(),
      pattern: /^[\p{Script=Latin}\p{N}\s.,\-'"()&/;„”]{2,100}$/u,
      message: t('invalid_company_name_format')
    },
    phoneNumber: {
      transform: (value) => {
        return value?.replace('+', '')?.trim()
      },
      pattern: /^\d{11,13}$/,
      message: t('invalid_phone_number_format')
    },
    personalCode: {
      transform: (value) => value?.trim(),
      pattern: /^\d{13}$/,
      message: t('invalid_personal_code_format')
    },
    legalPersonalCode: {
      transform: (value) => value?.trim(),
      validator: (_, value) => {
        if (!value) return Promise.resolve()
        if (SKIP_CHECK_IDENTIFICATION_NUMBERS.includes(String(value)))
          return Promise.resolve()

        if (!/^\d{13}$/.test(value))
          return Promise.reject(new Error(t('invalid_personal_code_format')))

        if (value[0] !== '1')
          return Promise.reject(new Error(t('invalid_idno')))

        const mask = '731731731731'
        let crc = 0
        for (let i = 0; i < 12; i++) {
          crc += Number.parseInt(value[i]) * Number.parseInt(mask[i])
        }
        if (crc % 10 !== Number.parseInt(value[12]))
          return Promise.reject(new Error(t('invalid_idno')))

        return Promise.resolve()
      }
    },
    customLegalPersonalCode: {
      transform: (value) => value?.trim(),
      validator: (_, value) => {
        const pattern = /^\d{12,15}$/
        if (!value) return Promise.resolve()
        if (SKIP_CHECK_IDENTIFICATION_NUMBERS.includes(String(value)))
          return Promise.resolve()
        if (pattern.test(value)) return Promise.resolve()

        return Promise.reject(
          new Error(t('invalid_medical_personal_code_format'))
        )
      }
    },
    vehicleRegistrationNumber: {
      transform: (value) => value?.trim(),
      pattern: /^.{4,}$/,
      message: t('invalid_vehicle_registration_number')
    },
    fiscalCode: {
      transform: (value) => value?.trim(),
      pattern: /^.{1,}$/,
      message: t('invalid_fiscal_code')
    },
    iban: {
      transform: (value) => value?.replace(/\s/g, '')?.toUpperCase(),
      pattern: /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/,
      message: t('invalid_iban_format')
    },
    minDate: (minDate?: Dayjs) => ({
      validator: (_: RuleObject, value: Dayjs) => {
        if (!value || !minDate) return Promise.resolve()

        if (value.isBefore(minDate, 'day')) {
          return Promise.reject(
            new Error(
              t('date_min_value', { date: minDate.format(DEFAULT_DATE_FORMAT) })
            )
          )
        }

        return Promise.resolve()
      }
    }),
    beforeYear: (minDate?: Dayjs) => ({
      validator: (_: RuleObject, value: Dayjs) => {
        if (!value || !minDate) return Promise.resolve()

        if (value.isBefore(minDate, 'year')) {
          return Promise.reject(
            new Error(t('year_min_value', { year: minDate?.year() }))
          )
        }

        return Promise.resolve()
      }
    }),
    olderThan: (age?: number) => ({
      validator: (_: RuleObject, value: Dayjs) => {
        if (!value || !age) return Promise.resolve()

        const today = dayjs()
        const limitDate = today.subtract(age, 'year')

        if (value.isAfter(limitDate, 'day')) {
          return Promise.reject(new Error(t('must_be_older_than', { age })))
        }

        return Promise.resolve()
      }
    }),
    minAmount: (minAmount?: number) => ({
      validator: (_: RuleObject, value: number) => {
        if (!value || !minAmount) return Promise.resolve()

        if (value <= minAmount) {
          return Promise.reject(
            new Error(t('amount_greater_than', { amount: minAmount }))
          )
        }

        return Promise.resolve()
      }
    }),
    maxAmount: (maxAmount?: number) => ({
      validator: (_: RuleObject, value: number) => {
        if (value === undefined || value === null || maxAmount === undefined)
          return Promise.resolve()

        if (value > maxAmount) {
          return Promise.reject(
            new Error(t('amount_max_value', { amount: maxAmount }))
          )
        }

        return Promise.resolve()
      }
    }),
    certificateNumberGreenCard: {
      transform: (value) => value?.trim(),
      validator: (_: RuleObject, value: string) => {
        if (!value) return Promise.resolve()

        if (/^\d/.test(value)) {
          if (!/^\d{9}$/.test(value)) {
            return Promise.reject(
              new Error(t('invalid_certificate_number_green_card_digit'))
            )
          }
          return Promise.resolve()
        }

        return Promise.reject(
          new Error(t('invalid_certificate_number_green_card_general'))
        )
      }
    },
    certificateNumberRCA: {
      transform: (value) => value?.trim()?.toUpperCase(),
      validator: (_: RuleObject, value: string) => {
        if (!value) return Promise.resolve()

        const trimmed = value.trim().toUpperCase()

        if (/^\d/.test(trimmed)) {
          if (!/^\d{9}$/.test(trimmed)) {
            return Promise.reject(
              new Error(t('invalid_certificate_number_rca_digit'))
            )
          }
          return Promise.resolve()
        }

        if (/^[A-Z]/.test(trimmed)) {
          if (trimmed.length > 10) {
            return Promise.reject(
              new Error(t('invalid_certificate_number_rca_letter'))
            )
          }
          return Promise.resolve()
        }

        return Promise.reject(
          new Error(t('invalid_certificate_number_rca_general'))
        )
      }
    }
  } satisfies ReturnValidations
}
